/**
 * Student Filtering Utilities
 *
 * These utilities ensure consistency when filtering students for various operations.
 * Use these functions to prevent locked, archived, or test students from being
 * selected for write operations like scheduling lessons, sending invitations, etc.
 *
 * Test Mode Support:
 * When testMode is enabled, the behavior changes:
 * - Only test students are shown
 * - Real students are considered "locked" for safety
 * - Test students become selectable for write operations
 */

/**
 * Filter students that are available for write operations
 * Excludes: locked, archived, and test students (or real students in test mode)
 * @param {Array} students - Array of student objects
 * @param {Boolean} testMode - Whether test mode is enabled
 * @returns {Array} Filtered array of students available for write operations
 */
export const filterAvailableStudents = (students, testMode = false) => {
	if (testMode) {
		// In test mode: only show test students that aren't archived
		return students.filter(student =>
			student.isTestStudent === true &&
			!student.archived &&
			!student.locked
		);
	}

	// Normal mode: exclude locked, archived, and test students
	return students.filter(student =>
		!student.locked &&
		!student.archived &&
		!student.isTestStudent
	);
};

/**
 * Filter students that are active and available
 * Excludes: locked, archived, test students, AND inactive students (or real students in test mode)
 * @param {Array} students - Array of student objects
 * @param {Boolean} testMode - Whether test mode is enabled
 * @returns {Array} Filtered array of active, available students
 */
export const filterActiveStudents = (students, testMode = false) => {
	if (testMode) {
		// In test mode: only show active test students
		return students.filter(student =>
			student.isTestStudent === true &&
			!student.archived &&
			!student.locked &&
			student.status === 'Active'
		);
	}

	// Normal mode: exclude locked, archived, test students, and inactive
	return students.filter(student =>
		!student.locked &&
		!student.archived &&
		!student.isTestStudent &&
		student.status === 'Active'
	);
};

/**
 * Check if a student can be modified (not locked)
 * @param {Object} student - Student object
 * @returns {Boolean} True if student can be modified
 */
export const canModifyStudent = (student) => {
	return student && !student.locked;
};

/**
 * Check if a student can be scheduled for lessons
 * @param {Object} student - Student object
 * @param {Boolean} testMode - Whether test mode is enabled
 * @returns {Boolean} True if student can be scheduled
 */
export const canScheduleStudent = (student, testMode = false) => {
	if (!student) return false;

	if (testMode) {
		// In test mode: only test students can be scheduled
		return student.isTestStudent === true &&
			!student.locked &&
			!student.archived;
	}

	// Normal mode: exclude locked, archived, and test students
	return !student.locked &&
		!student.archived &&
		!student.isTestStudent;
};

/**
 * Check if a student should show an inactive warning
 * @param {Object} student - Student object
 * @returns {Boolean} True if student is inactive
 */
export const isStudentInactive = (student) => {
	return student && student.status !== 'Active';
};

/**
 * Get student display name with status indicators
 * @param {Object} student - Student object
 * @param {Boolean} showStatus - Whether to show status indicators
 * @returns {String} Formatted student name with indicators
 */
export const getStudentDisplayName = (student, showStatus = true) => {
	const name = student.fullName || `${student.firstName} ${student.lastName}`;

	if (!showStatus) {
		return name;
	}

	const indicators = [];

	if (student.locked) indicators.push('🔒 Locked');
	if (student.archived) indicators.push('📦 Archived');
	if (student.isTestStudent) indicators.push('🧪 Test');
	if (student.status !== 'Active') indicators.push('⚠️ Inactive');

	return indicators.length > 0 ? `${name} (${indicators.join(', ')})` : name;
};

/**
 * Sort students with active students first
 * @param {Array} students - Array of student objects
 * @returns {Array} Sorted array with active students first
 */
export const sortStudentsByStatus = (students) => {
	return [...students].sort((a, b) => {
		// Active students first
		if (a.status === 'Active' && b.status !== 'Active') return -1;
		if (a.status !== 'Active' && b.status === 'Active') return 1;

		// Then sort alphabetically by last name
		const aName = a.lastName || a.fullName || '';
		const bName = b.lastName || b.fullName || '';
		return aName.localeCompare(bName);
	});
};

/**
 * Get filtering explanation text
 * @returns {String} Explanation of which students are filtered
 */
export const getFilterExplanation = () => {
	return 'Locked, archived, and test students are excluded from this list';
};
